import { useCallback } from 'react';
import { getConversations } from '../MessagesServices';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';

const useConversations = () => {
    const { handleError } = useErrorHandler();
    const fetchConversations = useCallback(async ({
        page,
        perPage,
        selectedChannel,
        setConversations,
        setHasNextPage,
        setHasPrevPage, // NEW: For upward pagination
        filters,
        setLoading,
        shouldAppend,
        shouldPrepend, // NEW: For upward pagination
        search_string,
        sort_by,
        sort_direction,
        after,
        before, // NEW: Cursor for loading older conversations
        setNextCursor,
        setPrevCursor, // NEW: For upward pagination
        signal // Add signal for request cancellation
    }) => {
        if (!selectedChannel) return;

        setLoading(true);
        try {
            const { data, metadata } = await getConversations({
                page: page,
                size: perPage,
                channel_id: selectedChannel,
                filters,
                search_string,
                sort_by,
                sort_direction,
                after,
                before, // NEW: Pass before cursor
                signal // Pass signal to API call
            });

            // Check if the request was aborted before updating state
            if (signal?.aborted) {
                return;
            }

            // Handle cursor updates based on direction
            if (shouldPrepend) {
                // Loading older conversations (scrolling up)
                if (setPrevCursor) {
                    setPrevCursor(metadata?.prev_cursor || null);
                }
                if (setHasPrevPage) {
                    setHasPrevPage(!!metadata?.prev_cursor || metadata?.has_prev || false);
                }
                setConversations(prev => [...data, ...prev]); // Prepend
            } else {
                // Loading newer conversations (scrolling down) or initial load
                if (setNextCursor) {
                    setNextCursor(metadata?.next_cursor || null);
                }
                setHasNextPage(!!metadata?.next_cursor || metadata?.has_next || false);
                setConversations(prev => shouldAppend ? [...prev, ...data] : data);
            }
        } catch (error) {
            // Don't handle error if request was cancelled
            if (error.name === 'CanceledError' || error.name === 'AbortError') {
                return;
            }
            handleError(error);
        } finally {
            // Only set loading to false if request wasn't aborted
            if (!signal?.aborted) {
                setLoading(false);
            }
        }
    }, [handleError]);

    return { fetchConversations };
};

export default useConversations;